import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Eye, Lock, LineChart } from "lucide-react";

/** Simple SVG line chart — real stored counts only, no smoothing tricks and
 *  no invented points. */
function Sparkline({ series }) {
  const w = 720, h = 160, pad = 8;
  const max = Math.max(1, ...series.map(p => p.count));
  const step = series.length > 1 ? (w - pad * 2) / (series.length - 1) : 0;
  const pts = series.map((p, i) => [
    pad + i * step,
    h - pad - (p.count / max) * (h - pad * 2),
  ]);
  const line = pts.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(" ");
  const area = `${pad},${h - pad} ${line} ${(pad + (series.length - 1) * step).toFixed(1)},${h - pad}`;
  const label = (iso) => new Date(iso + "T00:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric" });

  return (
    <div data-testid="views-trend-chart">
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-40" preserveAspectRatio="none" role="img"
           aria-label="Profile views over the last 30 days">
        <polygon points={area} fill="#2E6B57" opacity="0.10"/>
        <polyline points={line} fill="none" stroke="#245544" strokeWidth="2.5"
                  strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke"/>
        {pts.map(([x, y], i) => series[i].count > 0 && (
          <circle key={i} cx={x} cy={y} r="2.5" fill="#245544"/>
        ))}
      </svg>
      <div className="flex justify-between text-[11px] text-[#2D2926]/45 mt-1">
        <span>{label(series[0].date)}</span>
        <span>{label(series[Math.floor(series.length / 2)].date)}</span>
        <span>Today</span>
      </div>
      <p className="text-[11px] text-[#2D2926]/45 mt-2">Daily profile views · last 30 days</p>
    </div>
  );
}

export default function ProfileViewsPanel({ analytics, hasProPlus }) {
  const navigate = useNavigate();
  const [paused, setPaused] = useState(null);

  useEffect(() => {
    if (hasProPlus) return;
    api.get("/config/public")
      .then(r => setPaused(r.data.pro_plus_program?.enrollment_enabled === false))
      .catch(() => {});
  }, [hasProPlus]);

  const series = analytics.views_series || [];
  const hasData = series.some(p => p.count > 0);

  return (
    <section className="mb-6 bg-white rounded-2xl p-5 sm:p-6 shadow-sm border border-black/5" data-testid="profile-views-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-[#222]/50">
            <Eye size={14} className="text-[#2E6B57]"/> Profile views
          </div>
          <p className="text-3xl font-bold mt-1" style={{ fontFamily: "Outfit" }} data-testid="profile-views-total">
            {analytics.total_views ?? 0} <span className="text-base font-semibold text-[#2D2926]/45">total</span>
          </p>
        </div>
        {hasProPlus && (
          <div className="flex gap-6 text-right">
            <div data-testid="views-7d">
              <p className="text-[11px] uppercase tracking-wider text-[#222]/50">Last 7 days</p>
              <p className="text-xl font-bold" style={{ fontFamily: "Outfit" }}>{analytics.views_7d ?? 0}</p>
            </div>
            <div data-testid="views-30d">
              <p className="text-[11px] uppercase tracking-wider text-[#222]/50">Last 30 days</p>
              <p className="text-xl font-bold" style={{ fontFamily: "Outfit" }}>{analytics.views_30d ?? 0}</p>
            </div>
          </div>
        )}
      </div>

      <div className="mt-5 pt-5 border-t border-black/[0.06]">
        {hasProPlus ? (
          hasData ? (
            <Sparkline series={series}/>
          ) : (
            <div className="py-6 text-center" data-testid="views-trend-empty">
              <LineChart size={20} className="mx-auto text-[#2D2926]/25 mb-2"/>
              <p className="font-semibold text-sm" style={{ fontFamily: "Outfit" }}>Not enough activity yet</p>
              <p className="text-sm text-[#2D2926]/55 mt-1">
                Your profile view trend will appear here as neighbors discover your profile.
              </p>
            </div>
          )
        ) : (
          <div data-testid="views-trend-locked">
            <div className="flex items-center gap-2 flex-wrap">
              <h4 className="font-bold text-[15px]" style={{ fontFamily: "Outfit" }}>View trends</h4>
              <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-black/[0.06] text-[#2D2926]/55 flex items-center gap-1">
                <Lock size={9}/> Pro Plus
              </span>
            </div>
            <p className="text-sm text-[#2D2926]/60 mt-1">See how your profile visibility changes over time.</p>
            <button
              onClick={() => navigate("/upgrade")}
              className="mt-3 text-xs font-semibold text-[#245544] hover:underline"
              data-testid="views-trend-locked-cta"
            >
              {paused ? "Pro Plus is coming soon — see what's included →" : "Explore Pro Plus →"}
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
